/**
 * <s-product-image-zoom> — PDP image preview helper.
 *
 * Gallery buttons open one shared native dialog declaratively via commandfor/show-modal.
 * The dialog stacks every product image vertically so shoppers can scroll through them.
 * Zoom is mobile-only (below 768px); on desktop the gallery click does nothing.
 * This component:
 *   - closes the preview when the empty area around the photos is clicked
 *   - warms the hovered/focused image's high-resolution source so it is ready on open
 *   - scrolls the preview to the image that was clicked when the dialog opens
 *
 * Required markup:
 *   [data-zoom-preload][commandfor][data-zoom-target] — gallery trigger; data-zoom-target is the media id.
 *   dialog [data-zoom-scroller]                       — vertical scroll container inside the dialog.
 *   dialog [data-zoom-slide="<media id>"] img[data-zoom-image] — one slide per image.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];
  #pendingTarget = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.addEventListener('click', this.#handleClick, opts);

    for (const dialog of this.querySelectorAll('dialog')) {
      dialog.addEventListener('toggle', this.#handleToggle, opts);
      dialog.addEventListener('click', this.#handleDialogClick, opts);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    if (this.#isDesktop()) return;
    this.#preload(source);
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    // Zoom is mobile-only: on desktop widths, cancel the button's show-modal command.
    if (this.#isDesktop()) {
      event.preventDefault();
      return;
    }

    this.#pendingTarget = source.getAttribute('data-zoom-target');
  };

  #isDesktop() {
    return window.matchMedia('(min-width: 768px)').matches;
  }

  #handleToggle = (event) => {
    if (event.newState !== 'open') return;

    const dialog = event.currentTarget;
    const scroller = dialog.querySelector('[data-zoom-scroller]');
    if (!scroller) return;

    const slide = this.#pendingTarget
      ? dialog.querySelector(`[data-zoom-slide="${CSS.escape(this.#pendingTarget)}"]`)
      : null;
    this.#pendingTarget = null;

    const image = slide?.querySelector('img[data-zoom-image]');
    if (image instanceof HTMLImageElement) image.loading = 'eager';

    scroller.scrollTo({ top: slide instanceof HTMLElement ? slide.offsetTop : 0, behavior: 'instant' });
  };

  // Clicking the see-through area around the photos (not a photo or a control) closes the preview.
  #handleDialogClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('img, button, a')) return;

    const dialog = event.currentTarget;
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
  };

  #findSlideImage(source) {
    const dialogId = source.getAttribute('commandfor');
    const target = source.getAttribute('data-zoom-target');
    if (!dialogId || !target) return null;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector(`[data-zoom-slide="${CSS.escape(target)}"] img[data-zoom-image]`);
    return image instanceof HTMLImageElement ? image : null;
  }

  #preload(source) {
    const target = source.getAttribute('data-zoom-target');
    if (!target || this.#preloaded.has(target)) return;

    const image = this.#findSlideImage(source);
    if (!image) return;

    this.#preloaded.add(target);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
